import { createContext, useContext, useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useClientView } from "./ClientViewProvider";
import {
  buildLocalizedRuntimePath,
  buildAlternateLocaleUrls,
  parseClientViewPathname,
  switchLocaleInPathname,
} from "../utils/localeRoutes";
import { localeToUrlSegment } from "../../intl/manifestI18n";
import { normalizeLocale, readPersistedClientViewLocale } from "../../intl/localeResolution.js";

const ClientViewRouteLocaleContext = createContext(null);

export function ClientViewRouteLocaleProvider({ children }) {
  const { slug, manifest } = useClientView();
  const location = useLocation();
  const navigate = useNavigate();

  const parsed = useMemo(
    () => parseClientViewPathname(location.pathname, slug, manifest),
    [location.pathname, slug, manifest]
  );

  useEffect(() => {
    if (!parsed.unsupportedLocaleSegment) {
      return;
    }
    const fallback = buildLocalizedRuntimePath(
      slug,
      parsed.manifestI18n.defaultLocale,
      parsed.relativePathname.replace(/^\//, ""),
      location.search,
      manifest
    );
    navigate(fallback, { replace: true });
  }, [
    parsed.unsupportedLocaleSegment,
    parsed.manifestI18n.defaultLocale,
    parsed.relativePathname,
    slug,
    location.search,
    navigate,
    manifest,
  ]);

  useEffect(() => {
    if (!manifest || parsed.urlLocale) {
      return;
    }
    const cookieLocale = readPersistedClientViewLocale();
    if (!cookieLocale) {
      return;
    }
    const preferred = normalizeLocale(cookieLocale);
    const defaultLocale = parsed.manifestI18n.defaultLocale;
    if (!parsed.manifestI18n.supportedLocales.includes(preferred) || preferred === defaultLocale) {
      return;
    }
    const targetPath = buildLocalizedRuntimePath(
      slug,
      preferred,
      parsed.relativePathname,
      location.search,
      manifest
    );
    const current = `${location.pathname}${location.search || ""}`;
    if (targetPath !== current) {
      navigate(targetPath, { replace: true });
    }
  }, [
    manifest,
    parsed.urlLocale,
    parsed.relativePathname,
    parsed.manifestI18n,
    slug,
    location.pathname,
    location.search,
    navigate,
  ]);

  useEffect(() => {
    if (!parsed.urlLocale || !parsed.localeSegment || !manifest) {
      return;
    }
    const defaultLocale = parsed.manifestI18n.defaultLocale;
    if (parsed.urlLocale !== defaultLocale) {
      return;
    }
    const defaultSegment = localeToUrlSegment(defaultLocale, parsed.manifestI18n);
    if (!defaultSegment || parsed.localeSegment !== defaultSegment) {
      return;
    }
    const canonicalPath = buildLocalizedRuntimePath(
      slug,
      defaultLocale,
      parsed.relativePathname,
      "",
      manifest
    );
    const canonical = `${canonicalPath}${location.search || ""}`;
    const current = `${location.pathname}${location.search || ""}`;
    if (canonical !== current) {
      navigate(canonical, { replace: true });
    }
  }, [
    parsed.urlLocale,
    parsed.localeSegment,
    parsed.relativePathname,
    parsed.manifestI18n,
    slug,
    location.pathname,
    location.search,
    navigate,
    manifest,
  ]);

  const value = useMemo(
    () => ({
      ...parsed,
      slug,
      search: location.search,
      buildLocaleSwitchPath: (nextLocale) =>
        switchLocaleInPathname(location.pathname, location.search, slug, manifest, nextLocale),
      buildCanonicalUrl: (locale) =>
        typeof window !== "undefined"
          ? `${window.location.origin}${buildLocalizedRuntimePath(
              slug,
              locale,
              parsed.relativePathname,
              location.search
            )}`
          : null,
      alternateLocales: buildAlternateLocaleUrls(
        location.pathname,
        location.search,
        slug,
        manifest
      ),
    }),
    [parsed, slug, location.pathname, location.search, manifest]
  );

  return (
    <ClientViewRouteLocaleContext.Provider value={value}>
      {children}
    </ClientViewRouteLocaleContext.Provider>
  );
}

export function useClientViewRouteLocale() {
  const context = useContext(ClientViewRouteLocaleContext);
  if (!context) {
    throw new Error("useClientViewRouteLocale must be used within ClientViewRouteLocaleProvider");
  }
  return context;
}
